import type { RefObject } from "react";
import { useEffect, useState } from "react";

type UseInterstionParameters<T> = IntersectionObserverInit & {
  ref: RefObject<T | null>;
};

export const useIntersection = <T extends Element>({
  ref,
  rootMargin,
  threshold = 0.1,
  ...parameters
}: UseInterstionParameters<T>) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);

  useEffect(() => {
    if (ref.current === null) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isCurrentlyIntersecting = entry?.isIntersecting ?? false;
        setIsIntersecting(isCurrentlyIntersecting);
        if (isCurrentlyIntersecting && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      { rootMargin, threshold, ...parameters },
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, threshold, hasIntersected]);

  return {
    hasIntersected,
    isIntersecting,
  };
};
